import { useParams } from 'react-router-dom'
import { buildErrorMessage, ifcRootGuid } from '@constants'
import { AxiosError } from 'axios'
import { isGlbFile } from '@editor/hooks/requests/useIfcFiles'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { publishAIChatSceneEvent } from '@ui/events'
import { useSnackbar } from '@ui/snackbar'
import { getProjectFiles } from '@queries'
import { IFCUploadResponse, uploadIFCFile } from '@mutations'
import { retryUntilSuccessOrFailure } from 'src/state/utils'
import { AI_CHAT_UPLOAD_META } from '../../utils'

/**
 * Upload an IFC file the agent asked for in the chat and wait for its
 * conversion, like the IFC import page's upload. The chat lives outside the
 * editor's control store, so showing the new model is a scene event
 * (`AIChatSceneBridge`). The scene selects models by their converted GLB file,
 * a child of the uploaded IFC, so that is the one requested.
 */
const useChatIfcUpload = (onUploaded: () => void) => {
  const { projectId } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const queryClient = useQueryClient()

  return useMutation(
    async (file: File): Promise<string | undefined> => {
      const data: IFCUploadResponse = await uploadIFCFile.request(projectId!, file)
      await retryUntilSuccessOrFailure({ url: `/task-status/${data.task_id}` })
      // Fresh from the server (`staleTime: 0`), which also refreshes the IFC file lists shown.
      const files = await queryClient.fetchQuery<FilesResult>({
        queryKey: getProjectFiles.getKey(projectId!, ifcRootGuid),
        queryFn: async () =>
          (await getProjectFiles.request(projectId!, ifcRootGuid)) as unknown as FilesResult,
        staleTime: 0,
      })
      return files.files.find(
        candidate => candidate.parent_guid === data.ifc_file_guid && isGlbFile(candidate),
      )?.guid
    },
    {
      mutationKey: ['uploadIFCFile', projectId],
      meta: {
        title: 'IFC-Datei hochladen & konvertieren',
        description: 'Lädt eine IFC-Datei aus dem KI-Chat hoch und konvertiert sie.',
        ...AI_CHAT_UPLOAD_META,
      },
      onSuccess: glbFileGuid => {
        if (glbFileGuid) publishAIChatSceneEvent({ projectGuid: projectId!, glbFileGuid })
        enqueueSnackbar('IFC-Datei erfolgreich hochgeladen & konvertiert.', {
          variant: 'success',
        })
        onUploaded()
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Hochladen der IFC-Datei'), {
          variant: 'error',
        })
      },
    },
  )
}

export default useChatIfcUpload
